/**
 * The four pillar illustrations — each part of Wend, shown working, in one
 * looping 6-second scene. Inline SVG on the brand palette, so they scale to a
 * phone without a raster and cost no request.
 *
 * Decorative by contract: every one is `aria-hidden`, because the section's
 * heading and line already say what the picture shows, and a screen reader
 * gains nothing from "Priya S., ad, new lead" read out of a drawing. The suite
 * checks the attribute on all four.
 *
 * Honest by the same rules as the copy. The figures in each scene are made up
 * (the "example" chip each one carried was removed at the owner's request,
 * Oct 2026). Reach's ad is published paused and started by
 * a person; Sense's finding offers a draft and nothing presses it; Flow's
 * person reaches the goal and the journey stops. Channels appear as small
 * icons, never as the subject.
 *
 * The motion lives in index.css (reach-*, connect-*, sense-*, flow-*). Under
 * prefers-reduced-motion it all stops and the resting state of each shape is
 * the scene's finished frame — the person landed, the reply in, the finding up,
 * the goal reached.
 */
import type { ReactNode } from 'react';

const W = 400;
const H = 320;

const C = {
  ink: '#0A0818',
  s1: '#120E28',
  s2: '#191338',
  s3: '#221A47',
  line: 'rgba(255,255,255,0.10)',
  lineStrong: 'rgba(255,255,255,0.18)',
  text: '#ECEAF8',
  muted: '#9F9AC2',
  a: '#6D5EF6',
  b: '#9B5EF6',
  brandInk: '#C6BCFF',
  good: '#3DD68C',
  warn: '#F5B851',
  bad: '#F2708A',
  identA: '#8FB2FF',
  identC: '#F5966B',
  identD: '#6FD6C6',
} as const;

const SANS = 'Instrument Sans, ui-sans-serif, system-ui, sans-serif';
const MONO = 'JetBrains Mono, ui-monospace, monospace';

function Frame({ name, children }: { name: string; children: ReactNode }) {
  return (
    <div
      aria-hidden="true"
      data-illustration={name}
      className="illo relative overflow-hidden rounded-xl border border-line-strong bg-surface-1 shadow-lift"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-dot-grid opacity-60" />
      <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 size-64 rounded-full bg-brand-a/20 blur-3xl" />
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" className="relative block h-auto" focusable="false" style={{ fontFamily: SANS }}>
        {children}
      </svg>
    </div>
  );
}

function Chip({ x, y, w, label }: { x: number; y: number; w: number; label: string }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={24} rx={12} fill={C.s3} stroke={C.line} />
      <text x={x + 12} y={y + 16.5} fontSize={12} fill={C.muted}>
        {label}
      </text>
    </g>
  );
}

function ChatIcon({ x, y, color = C.good }: { x: number; y: number; color?: string }) {
  return <path d={`M${x} ${y + 10} l1.4 -3.6 a5.6 5.6 0 1 1 2.4 2.4 z`} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" />;
}

function MailIcon({ x, y, color = C.identA }: { x: number; y: number; color?: string }) {
  return (
    <g fill="none" stroke={color} strokeWidth={1.4} strokeLinejoin="round">
      <rect x={x} y={y} width={12} height={9} rx={1.6} />
      <path d={`M${x} ${y + 1.2} l6 4 l6 -4`} />
    </g>
  );
}

/* -------------------------------------------------------------------------- */
/* Reach: ads go out; a tap on one lands as a person in People, tagged "ad".   */
/* -------------------------------------------------------------------------- */

function AdCard({ x, y, front }: { x: number; y: number; front: boolean }) {
  return (
    <g>
      <rect x={x} y={y} width={150} height={196} rx={14} fill={front ? C.s2 : C.s3} stroke={front ? C.lineStrong : C.line} />
      {front && (
        <>
          <rect x={x + 12} y={y + 12} width={126} height={84} rx={9} fill="url(#reach-img)" />
          <circle cx={x + 108} cy={y + 38} r={13} fill="rgba(255,255,255,0.35)" />
          <path d={`M${x + 12} ${y + 82} l34 -26 l26 18 l22 -14 l44 30 v6 h-126 z`} fill="rgba(10,8,24,0.28)" />
          <text x={x + 12} y={y + 116} fontSize={13.5} fontWeight={600} fill={C.text}>
            Diwali offer · Pune
          </text>
          <text x={x + 12} y={y + 133} fontSize={11} fill={C.muted}>
            Sponsored
          </text>
          <rect x={x + 12} y={y + 146} width={126} height={36} rx={9} fill={C.a} />
          <text x={x + 75} y={y + 168.5} textAnchor="middle" fontSize={12.5} fontWeight={600} fill="#fff">
            Send message
          </text>
        </>
      )}
    </g>
  );
}

function PersonRow({ y, name, sub, color }: { y: number; name: string; sub: string; color: string }) {
  return (
    <g>
      <circle cx={244} cy={y + 20} r={11} fill={color} fillOpacity={0.35} stroke={color} strokeOpacity={0.6} />
      <text x={262} y={y + 17} fontSize={12.5} fill={C.text}>
        {name}
      </text>
      <text x={262} y={y + 32} fontSize={10.5} fill={C.muted}>
        {sub}
      </text>
    </g>
  );
}

export function ReachIllustration() {
  return (
    <Frame name="reach">
      <defs>
        <linearGradient id="reach-img" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={C.identC} />
          <stop offset="1" stopColor={C.b} />
        </linearGradient>
        <linearGradient id="reach-avatar" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F2A65A" />
          <stop offset="1" stopColor="#E0607E" />
        </linearGradient>
      </defs>
      <Chip x={16} y={14} w={168} label="◇  Your Meta ad account" />

      {/* The ad, and the ones behind it, drifting. */}
      <g opacity={0.45} transform="rotate(-7 99 156)">
        <g className="reach-float-2">
          <AdCard x={24} y={58} front={false} />
        </g>
      </g>
      <g opacity={0.65} transform="rotate(5 99 156)">
        <g className="reach-float-2">
          <AdCard x={24} y={58} front={false} />
        </g>
      </g>
      <g className="reach-float">
        <AdCard x={24} y={58} front />
        <circle className="reach-tap" cx={128} cy={222} r={10} fill="none" stroke="#fff" strokeWidth={2.5} />
      </g>

      {/* The tap travels to People. */}
      <line x1={180} y1={222} x2={218} y2={222} stroke={C.b} strokeOpacity={0.5} strokeWidth={2} strokeDasharray="3 4" />
      <circle className="reach-dot" cx={182} cy={222} r={5} fill={C.b} />

      {/* People. */}
      <rect x={222} y={58} width={162} height={232} rx={14} fill={C.s1} stroke={C.lineStrong} />
      <text x={236} y={82} fontSize={13.5} fontWeight={600} fill={C.text}>
        People
      </text>
      <text className="reach-count-old" x={370} y={82} textAnchor="end" fontFamily={MONO} fontSize={11.5} fill={C.muted}>
        1,204
      </text>
      <text className="reach-count-new" x={370} y={82} textAnchor="end" fontFamily={MONO} fontSize={11.5} fill={C.good}>
        1,205
      </text>
      <line x1={232} y1={94} x2={374} y2={94} stroke={C.line} />
      <PersonRow y={100} name="Rohan M." sub="from an upload" color={C.identA} />
      <PersonRow y={146} name="Meera K." sub="from an upload" color={C.identD} />
      <g className="reach-land">
        <rect x={230} y={194} width={146} height={52} rx={10} fill="rgba(109,94,246,0.16)" stroke="rgba(155,94,246,0.6)" />
        <circle cx={244} cy={220} r={11} fill="url(#reach-avatar)" />
        <text x={262} y={215} fontSize={12.5} fontWeight={600} fill={C.text}>
          Priya S.
        </text>
        <rect x={262} y={223} width={28} height={16} rx={8} fill="rgba(109,94,246,0.35)" />
        <text x={276} y={234.5} textAnchor="middle" fontFamily={MONO} fontSize={9.5} fill={C.brandInk}>
          ad
        </text>
        <rect x={294} y={223} width={64} height={16} rx={8} fill="rgba(61,214,140,0.14)" />
        <text x={326} y={234.5} textAnchor="middle" fontFamily={MONO} fontSize={9.5} fill={C.good}>
          new lead
        </text>
        <text x={236} y={272} fontFamily={MONO} fontSize={10} fill={C.muted}>
          ad_id · 23851240117
        </text>
      </g>

      <text x={24} y={290} fontSize={11.5} fill={C.muted}>
        Published paused · you press start
      </text>
    </Frame>
  );
}

/* -------------------------------------------------------------------------- */
/* Connect: a segment's people fan out into messages; ticks move to replied.   */
/* -------------------------------------------------------------------------- */

const CONNECT_ROWS = [
  { name: 'Asha R.', channel: 'chat', replies: true, color: C.identC },
  { name: 'Vikram P.', channel: 'mail', replies: false, color: C.identA },
  { name: 'Neha J.', channel: 'chat', replies: true, color: C.identD },
  { name: 'Karan S.', channel: 'chat', replies: false, color: C.b },
] as const;

export function ConnectIllustration() {
  const rowY = (index: number): number => 58 + index * 54;
  return (
    <Frame name="connect">
      <Chip x={16} y={14} w={176} label="◫  Lapsing buyers · 312" />

      {/* The segment: its people. */}
      <rect x={16} y={58} width={110} height={206} rx={14} fill={C.s1} stroke={C.lineStrong} />
      {Array.from({ length: 12 }, (_, index) => {
        const col = index % 3;
        const row = Math.floor(index / 3);
        const palette = [C.identA, C.identC, C.identD, C.b];
        const color = palette[(index + row) % palette.length] ?? C.b;
        return <circle key={index} cx={43 + col * 28} cy={90 + row * 34} r={10} fill={color} fillOpacity={0.3} stroke={color} strokeOpacity={0.55} />;
      })}
      <text x={71} y={246} textAnchor="middle" fontSize={11.5} fill={C.muted}>
        312 people
      </text>

      {/* The fan. */}
      {CONNECT_ROWS.map((_, index) => {
        const y = rowY(index) + 22;
        return (
          <path
            key={index}
            className="connect-wire"
            d={`M126 160 C 140 160, 138 ${y}, 152 ${y}`}
            fill="none"
            stroke={C.b}
            strokeOpacity={0.55}
            strokeWidth={1.6}
            strokeDasharray="4 4"
          />
        );
      })}

      {/* The messages. */}
      {CONNECT_ROWS.map((row, index) => {
        const y = rowY(index);
        const delay = `${index * 0.35}s`;
        return (
          <g key={row.name} className="connect-row" style={{ animationDelay: delay }}>
            <rect x={152} y={y} width={232} height={44} rx={10} fill={C.s2} stroke={C.line} />
            <circle cx={172} cy={y + 22} r={10} fill={row.color} fillOpacity={0.32} stroke={row.color} strokeOpacity={0.6} />
            <text x={190} y={y + 19} fontSize={12.5} fill={C.text}>
              {row.name}
            </text>
            {row.channel === 'chat' ? <ChatIcon x={190} y={y + 25} /> : <MailIcon x={190} y={y + 26} />}
            <text x={207} y={y + 35} fontSize={10} fill={C.muted}>
              {row.channel === 'chat' ? 'WhatsApp' : 'Email'}
            </text>
            <text className="connect-s1" style={{ animationDelay: delay }} x={372} y={y + 27} textAnchor="end" fontFamily={MONO} fontSize={10.5} fill={C.muted}>
              ✓ sent
            </text>
            <text
              className={row.replies ? 'connect-s2' : 'connect-s2-stay'}
              style={{ animationDelay: delay }}
              x={372}
              y={y + 27}
              textAnchor="end"
              fontFamily={MONO}
              fontSize={10.5}
              fill={C.identA}
            >
              ✓✓ delivered
            </text>
            {row.replies && (
              <g className="connect-s3" style={{ animationDelay: delay }}>
                <rect x={304} y={y + 13} width={72} height={18} rx={9} fill="rgba(61,214,140,0.14)" stroke="rgba(61,214,140,0.5)" />
                <text x={340} y={y + 26} textAnchor="middle" fontFamily={MONO} fontSize={10} fill={C.good}>
                  ↩ replied
                </text>
              </g>
            )}
          </g>
        );
      })}

      <text x={16} y={296} fontSize={11.5} fill={C.muted}>
        From your own accounts · quiet hours · frequency cap
      </text>
    </Frame>
  );
}

/* -------------------------------------------------------------------------- */
/* Sense: the funnel fills, the weak step is flagged, a finding offers a draft. */
/* -------------------------------------------------------------------------- */

const FUNNEL = [
  { label: 'Sent', value: '163', w: 180, color: C.a },
  { label: 'Delivered', value: '160', w: 176, color: C.a },
  { label: 'Read', value: '134', w: 148, color: C.b },
  { label: 'Replied', value: '19', w: 22, color: C.warn },
] as const;

export function SenseIllustration() {
  return (
    <Frame name="sense">
      <Chip x={16} y={14} w={150} label="Welcome · new leads" />

      {FUNNEL.map((row, index) => {
        const y = 60 + index * 28;
        return (
          <g key={row.label}>
            <text x={16} y={y + 12.5} fontSize={12} fill={C.muted}>
              {row.label}
            </text>
            <rect x={96} y={y} width={180} height={16} rx={5} fill="rgba(255,255,255,0.05)" />
            <rect className="sense-bar" style={{ animationDelay: `${index * 0.12}s` }} x={96} y={y} width={row.w} height={16} rx={5} fill={row.color} fillOpacity={0.85} />
            <text x={96 + row.w + 8} y={y + 12.5} fontFamily={MONO} fontSize={11} fill={C.text}>
              {row.value}
            </text>
          </g>
        );
      })}
      <g className="sense-flag">
        <circle cx={160} cy={152} r={9} fill="rgba(245,184,81,0.18)" stroke={C.warn} strokeWidth={1.5} />
        <text x={160} y={156} textAnchor="middle" fontSize={11} fontWeight={700} fill={C.warn}>
          !
        </text>
      </g>

      <text x={16} y={222} fontSize={12} fill={C.muted}>
        Sense drafts.
      </text>
      <text x={16} y={240} fontSize={12} fontWeight={600} fill={C.text}>
        You decide.
      </text>

      <g className="sense-card">
        <rect x={120} y={178} width={264} height={124} rx={14} fill={C.s2} stroke="rgba(245,184,81,0.45)" />
        <text x={136} y={200} fontFamily={MONO} fontSize={10} letterSpacing={1.2} fill={C.warn}>
          ◆ SENSE · TODAY
        </text>
        <text x={136} y={221} fontSize={14.5} fontWeight={600} fill={C.text}>
          115 leads not replying
        </text>
        <text x={136} y={249} fontSize={20} fontWeight={600} fill={C.text}>
          ₹41,200
        </text>
        <text x={222} y={249} fontSize={11.5} fill={C.muted}>
          at stake
        </text>
        <rect x={136} y={260} width={118} height={30} rx={8} fill="url(#sense-cta)" />
        <rect className="sense-cta" x={133} y={257} width={124} height={36} rx={10} fill="none" stroke={C.b} strokeWidth={2} strokeOpacity={0.6} />
        <text x={195} y={279.5} textAnchor="middle" fontSize={12.5} fontWeight={600} fill="#fff">
          Draft a flow
        </text>
        <text x={272} y={279.5} fontSize={12} fill={C.muted}>
          Dismiss
        </text>
      </g>
      <defs>
        <linearGradient id="sense-cta" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={C.a} />
          <stop offset="1" stopColor={C.b} />
        </linearGradient>
      </defs>
    </Frame>
  );
}

/* -------------------------------------------------------------------------- */
/* Flow: a person travels Start → Message → Wait → Condition → Goal.           */
/* -------------------------------------------------------------------------- */

export function FlowIllustration() {
  const edge = { fill: 'none', stroke: 'rgba(236,234,248,0.4)', strokeWidth: 1.8 } as const;
  const lit = { fill: 'none', stroke: C.b, strokeWidth: 2.5 } as const;
  return (
    <Frame name="flow">
      <defs>
        <linearGradient id="flow-start" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={C.a} />
          <stop offset="1" stopColor={C.b} />
        </linearGradient>
        <filter id="flow-glow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
      </defs>
      <Chip x={16} y={14} w={128} label="Welcome leads" />

      {/* The graph, inset so the Goal and Reminder cards clear the frame's right edge. */}
      <g transform="translate(12 8) scale(0.92)">
      {/* Edges. */}
      <line x1={54} y1={150} x2={66} y2={150} {...edge} />
      <line x1={150} y1={150} x2={166} y2={150} {...edge} />
      <line x1={206} y1={150} x2={230} y2={150} {...edge} />
      <path d="M270 150 C 288 150, 286 86, 304 86" {...edge} />
      <path d="M270 150 C 288 150, 286 218, 304 218" {...edge} />
      <path className="flow-lit-goal" d="M270 150 C 288 150, 286 86, 304 86" fill="none" stroke={C.good} strokeWidth={2.5} />
      <text x={300} y={130} fontFamily={MONO} fontSize={10.5} fill={C.good}>
        yes
      </text>
      <text x={300} y={180} fontFamily={MONO} fontSize={10.5} fill={C.bad}>
        no
      </text>

      {/* Start. */}
      <circle cx={36} cy={150} r={18} fill="url(#flow-start)" />
      <path d="M31 142 L43 150 L31 158 Z" fill="#fff" />
      <circle className="flow-lit-start" cx={36} cy={150} r={23} {...lit} />
      <text x={36} y={198} textAnchor="middle" fontSize={11.5} fill={C.muted}>
        Start
      </text>

      {/* Message. */}
      <rect x={66} y={126} width={84} height={48} rx={10} fill={C.s2} stroke={C.lineStrong} />
      <ChatIcon x={75} y={138} />
      <text x={91} y={147} fontSize={11.5} fontWeight={600} fill={C.text}>
        Welcome
      </text>
      <text x={76} y={164} fontSize={10} fill={C.muted}>
        2 buttons
      </text>
      <rect className="flow-lit-msg" x={62} y={122} width={92} height={56} rx={13} {...lit} />
      <text x={108} y={198} textAnchor="middle" fontSize={11.5} fill={C.muted}>
        Message
      </text>

      {/* Wait. */}
      <circle cx={186} cy={150} r={20} fill={C.s3} stroke={C.lineStrong} />
      <circle cx={186} cy={150} r={10} fill="none" stroke={C.text} strokeWidth={1.6} />
      <line className="flow-hand" x1={0} y1={0} x2={0} y2={-7} stroke={C.text} strokeWidth={1.8} strokeLinecap="round" transform="translate(186 150)" />
      <circle className="flow-lit-wait" cx={186} cy={150} r={25} {...lit} />
      <text x={186} y={198} textAnchor="middle" fontSize={11.5} fill={C.muted}>
        Wait 1 day
      </text>

      {/* Condition. */}
      <path d="M250 128 L272 150 L250 172 L228 150 Z" fill={C.s3} stroke={C.lineStrong} />
      <text x={250} y={155} textAnchor="middle" fontSize={13} fontWeight={700} fill={C.text}>
        ?
      </text>
      <path className="flow-lit-cond" d="M250 122 L278 150 L250 178 L222 150 Z" {...lit} />
      <text x={250} y={198} textAnchor="middle" fontSize={11.5} fill={C.muted}>
        Replied?
      </text>

      {/* Goal. */}
      <rect x={304} y={64} width={84} height={44} rx={10} fill="rgba(61,214,140,0.10)" stroke="rgba(61,214,140,0.5)" />
      <path d="M316 98 V74 h14 l-3 5 l3 5 h-14" fill="none" stroke={C.good} strokeWidth={1.6} strokeLinejoin="round" />
      <text x={336} y={83} fontSize={12} fontWeight={600} fill={C.text}>
        Goal
      </text>
      <text x={336} y={98} fontSize={10} fill={C.muted}>
        an order
      </text>
      <rect className="flow-lit-goal" x={300} y={60} width={92} height={52} rx={13} fill="rgba(61,214,140,0.10)" stroke={C.good} strokeWidth={2.5} />
      <text className="flow-lit-goal" x={346} y={54} textAnchor="middle" fontFamily={MONO} fontSize={10.5} fill={C.good}>
        converted ✓
      </text>

      {/* The other path. */}
      <rect x={304} y={196} width={84} height={44} rx={10} fill={C.s2} stroke={C.line} />
      <ChatIcon x={314} y={208} />
      <text x={332} y={223} fontSize={12} fill={C.text}>
        Reminder
      </text>

      {/* The person. */}
      <g className="flow-dot">
        <circle r={9} fill={C.b} opacity={0.7} filter="url(#flow-glow)" />
        <circle r={6} fill="#fff" />
      </g>
      </g>

      <text x={16} y={290} fontSize={11.5} fill={C.muted}>
        You launch it · quiet hours and caps on every step
      </text>
    </Frame>
  );
}
